import React, { useState, useEffect } from 'react'
import { Form, Input, Select, Button, Card, Row, Col, InputNumber, Divider, message, Spin } from 'antd'
import axios from 'util/Api'
import { getErrorMessage } from 'util/methods'

const { Option } = Select

const IotSetting = ({ match, form, assetData }) => {

    const { getFieldDecorator, setFieldsValue, getFieldsValue } = form
    const asset = match && match.params && match.params.asset

    // Form state management
    const [formState, setFormState] = useState({
        ignition: 1,
        movementAccelerometerVariance: null,
        gnssSpeed: null,
        energyMeterReading: null,
        movement: null,
        delay_seconds: 60,
        eyeBeaconParameters: []
    })

    const [eyeBeaconFields, setEyeBeaconFields] = useState([])
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)

    // Predefined eye beacon field options
    const eyeBeaconOptions = [
        { id: 1, name: 'Eye Beacon 1', value: 'eye_bea_mov_1' },
        { id: 2, name: 'Eye Beacon 2', value: 'eye_bea_mov_2' }
    ]

    // Fetch IoT idle setting data from API
    const fetchIotIdleSetting = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`/iot-idle-setting/asset/${asset}`)
            setData(response.data)
            // Update form state with fetched data
            const { ignition, movementAccelerometerVariance, gnssSpeed,
                energyMeterReading, movement, delay_seconds,
                eyeBeaconParameters } = response.data || {}
            const newFormState = {
                ignition: response.data ? ignition : 1,
                movementAccelerometerVariance: response.data ? movementAccelerometerVariance : null,
                gnssSpeed: response.data ? gnssSpeed : null,
                energyMeterReading: response.data ? energyMeterReading : null,
                movement: response.data ? movement : 0,
                delay_seconds: response.data ? delay_seconds : 60,
                eyeBeaconParameters: response.data ? eyeBeaconParameters : []
            }

            setFormState(newFormState)
            // console.log('newFormState', newFormState)

            // Update eye beacon fields if data exists
            // console.log('eyeBeaconParameters', eyeBeaconParameters)
            if (eyeBeaconParameters && eyeBeaconParameters.length > 0) {
                const beaconFields = eyeBeaconParameters.map((param, index) => ({
                    id: index + 1,
                    fieldName: param.fieldName || param.name, // Handle both fieldName and name
                    value: param.value
                }))
                setEyeBeaconFields(beaconFields)
            } else {
                // Initialize with one empty eye beacon field if no data exists
                setEyeBeaconFields([{
                    id: 1,
                    fieldName: eyeBeaconOptions[0].value,
                    value: ''
                }])
            }

            // Populate form fields
            setFieldsValue(newFormState)
            // console.log('setFieldsValue', setFieldsValue)
            // message.success('IoT settings loaded successfully!')
        } catch (error) {
            if (error.response && error.response.status === 404) {
                // No existing settings found, use defaults
                console.log('No existing IoT settings found, using defaults')
                setFieldsValue(formState)
            } else {
                console.error('Error fetching IoT idle setting:', error)
                message.error('Failed to load IoT settings. Using default values.')
                setFieldsValue(formState)
            }
        } finally {
            setLoading(false)
        }
    }

    // Initialize form on component mount
    useEffect(() => {
        if (asset) {
            fetchIotIdleSetting()
        }
    }, [asset])

    // Update form when formState changes
    useEffect(() => {
        if (!loading) {
            setFieldsValue(formState)
        }
    }, [formState, loading])

    const addEyeBeaconField = () => {
        const newId = eyeBeaconFields.length > 0 ? Math.max(...eyeBeaconFields.map(field => field.id)) + 1 : 1
        const availableOptions = eyeBeaconOptions.filter(option =>
            !eyeBeaconFields.some(field => field.fieldName === option.value)
        )

        if (availableOptions.length > 0) {
            const newField = {
                id: newId,
                fieldName: availableOptions[0].value,
                value: ''
            }
            setEyeBeaconFields([...eyeBeaconFields, newField])
        }
    }

    const removeEyeBeaconField = (id) => {
        setEyeBeaconFields(eyeBeaconFields.filter(field => field.id !== id))
    }

    const updateEyeBeaconField = (id, fieldName, value) => {
        const updatedFields = eyeBeaconFields.map(field =>
            field.id === id ? { ...field, fieldName, value } : field
        )
        // console.log('updatedFields', updatedFields)
        setEyeBeaconFields(updatedFields)

        // Update form state
        setFormState(prev => ({
            ...prev,
            eyeBeaconParameters: updatedFields
        }))

        // console.log('Updated eye beacon field:', { id, fieldName, value })
        // console.log('Updated fields:', updatedFields)
    }

    const onFinish = async (values) => {
        try {
            // console.log('asset', asset)
            // Prepare final data with eye beacon parameters
            const formData = {
                ...values,
                asset,
                eyeBeaconParameters: eyeBeaconFields.map(field => ({
                    fieldName: field.fieldName,
                    name: field.fieldName,
                    value: field.value
                })).filter(field => field.value !== null && field.value !== undefined && field.value !== '')
            }

            // console.log('Form submitted:', formData)
            // console.log('Eye beacon fields state:', eyeBeaconFields)
            // console.log('Eye beacon fields with values:', eyeBeaconFields.filter(field => field.value))
            // console.log('Eye beacon fields mapping:', eyeBeaconFields.map(field => ({
            //     fieldName: field.fieldName,
            //     value: field.value
            // })).filter(field => field.value))

            // Make API call to save the data
            const response = await axios.post('/iot-idle-setting', formData)

            message.success('IoT Settings saved successfully!')
            // console.log('API Response:', response.data)

            // Refresh the form data from server
            fetchIotIdleSetting()
        } catch (error) {
            console.error('Error saving IoT settings:', error)
            const errorMessage = getErrorMessage(error)
            // message.error(errorMessage)
            console.log('errorMessage', errorMessage)
        }
    }

    const onFinishFailed = (errorInfo) => {
        console.log('Form validation failed:', errorInfo)
        console.log('Error fields:', errorInfo.errorFields)
        console.log('Values:', errorInfo.values)
        message.error('Please check the form for errors')
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        form.validateFields((err, values) => {
            if (!err) {
                onFinish(values)
            } else {
                onFinishFailed({ errorFields: err, values })
            }
        })
    }


    return (
        <div style={{ padding: '24px' }}>

            <h3>{`${data ? 'Update' : 'Create'} Device Idle Setting (${assetData && assetData.assetCode || '-'})`}</h3>
            <Form
                layout="vertical"
                onSubmit={handleSubmit}
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin tip="Loading IoT settings..." size="large" />
                    </div>
                ) : (
                    <>
                        {/* Ignition Field */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Ignition (Taken from Ignition Setting)">
                                    {getFieldDecorator('ignition', {
                                        initialValue: formState.ignition,
                                        rules: [
                                            { required: true, message: 'Ignition value is required' },
                                            { type: 'number', min: 1, max: 1, message: 'Ignition must be a positive number' }
                                        ]
                                    })(
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="Effective Ignition"
                                            min={1}
                                            max={1}
                                            disabled={true}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Movement Accelerometer Variance Field */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Movement Accelerometer Variance">
                                    {getFieldDecorator('movementAccelerometerVariance', {
                                        initialValue: formState.movementAccelerometerVariance,
                                        rules: [
                                            { type: 'number', message: 'Movement accelerometer variance must be a number' }
                                        ]
                                    })(
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="Enter movement accelerometer variance"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* GNSS Speed Field */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="GNSS Speed">
                                    {getFieldDecorator('gnssSpeed', {
                                        initialValue: formState.gnssSpeed,
                                        rules: [
                                            { type: 'number', message: 'GNSS Speed must be a number' }
                                        ]
                                    })(
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="Enter GNSS speed value"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Energy Meter Reading Field */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Energy Meter Reading">
                                    {getFieldDecorator('energyMeterReading', {
                                        initialValue: formState.energyMeterReading,
                                        rules: [
                                            { type: 'number', message: 'Energy meter reading must be a number' }
                                        ]
                                    })(
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="Enter energy meter reading"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Movement Field */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Movement">
                                    {getFieldDecorator('movement', {
                                        initialValue: formState.movement,
                                        rules: [
                                            // { required: true, message: 'Movement value is required' },
                                            { type: 'number', max: 1, message: 'Movement must be a 0, 1 or empty' }
                                        ]
                                    })(
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="Enter movement value"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* Eye Beacon Parameters - Dynamic Fields */}
                        <Form.Item label="Eye Beacon Parameters">
                            {eyeBeaconFields.map((field, index) => (
                                <Row gutter={16} key={field.id} style={{ marginBottom: '16px' }}>
                                    <Col span={8}>
                                        <Select
                                            value={field.fieldName}
                                            onChange={(value) => updateEyeBeaconField(field.id, value, field.value)}
                                            placeholder="Select field"
                                            style={{ width: '100%' }}
                                        >
                                            {eyeBeaconOptions.map(option => (
                                                <Option
                                                    key={option.value}
                                                    value={option.value}
                                                    disabled={eyeBeaconFields.some(f => f.id !== field.id && f.fieldName === option.value)}
                                                >
                                                    {option.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col span={10}>
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="Enter value"
                                            value={field.value}
                                            onChange={(value) => updateEyeBeaconField(field.id, field.fieldName, value)}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Button
                                            type="text"
                                            onClick={() => removeEyeBeaconField(field.id)}
                                            disabled={eyeBeaconFields.length === 0}
                                            danger
                                        >
                                            Remove
                                        </Button>
                                    </Col>
                                </Row>
                            ))}
                            <Button
                                type="dashed"
                                onClick={addEyeBeaconField}
                                style={{ width: '100%' }}
                                disabled={eyeBeaconFields.length >= eyeBeaconOptions.length}
                            >
                                + Add Eye Beacon Parameter
                            </Button>
                        </Form.Item>

                        {/* Delay Idle Change Field */}
                        <Form.Item style={{ marginTop: '24px' }}>
                            <Card
                                size="small"
                                style={{
                                    backgroundColor: '#f6f8fa',
                                    border: '1px solid #d0d7de',
                                    marginBottom: '16px'
                                }}
                            >
                                <div style={{ fontSize: '14px', color: '#24292f' }}>
                                    <strong>Delay Idle change by:</strong>
                                    <div style={{ marginTop: '16px' }}>
                                        <Form.Item label="Delay">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                {getFieldDecorator('delay_seconds', {
                                                    initialValue: formState.delay_seconds,
                                                    rules: [
                                                        { required: true, message: 'Delay seconds is required' },
                                                        { type: 'number', min: 1, message: 'Delay must be a positive number' }
                                                    ]
                                                })(
                                                    <InputNumber
                                                        style={{ width: '200px' }}
                                                        placeholder="Enter delay"
                                                        min={1}
                                                    />
                                                )}
                                                <span style={{ marginLeft: '8px', fontSize: '14px', color: '#24292f' }}>seconds</span>
                                            </div>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Card>
                        </Form.Item>

                        {/* AND Logic Visualization */}
                        <Form.Item style={{ marginTop: '24px' }}>
                            <Card
                                size="small"
                                style={{
                                    backgroundColor: '#f6f8fa',
                                    border: '1px solid #d0d7de',
                                    marginBottom: '16px'
                                }}
                            >
                                <div style={{ fontSize: '14px', color: '#24292f' }}>
                                    <strong>Asset will be considered as IDLE when:</strong>
                                    <div style={{
                                        marginTop: '8px',
                                        padding: '12px',
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid #d0d7de',
                                        fontFamily: 'monospace',
                                        fontSize: '13px'
                                    }}>
                                        {(() => {
                                            const conditions = []

                                            // Add ignition condition if it has a value
                                            if (formState.ignition !== null && formState.ignition !== undefined) {
                                                conditions.push(`ignition = ${formState.ignition}`)
                                            }

                                            // Add movement accelerometer variance condition if it has a value
                                            if (formState.movementAccelerometerVariance !== null && formState.movementAccelerometerVariance !== undefined) {
                                                conditions.push(`movement_accelerometer_variance < ${formState.movementAccelerometerVariance}`)
                                            }

                                            // Add GNSS speed condition if it has a value
                                            if (formState.gnssSpeed !== null && formState.gnssSpeed !== undefined) {
                                                conditions.push(`gnss_speed < ${formState.gnssSpeed}`)
                                            }

                                            // Add energy meter reading condition if it has a value
                                            if (formState.energyMeterReading !== null && formState.energyMeterReading !== undefined) {
                                                conditions.push(`energy_meter_reading < ${formState.energyMeterReading}`)
                                            }

                                            // Add movement condition if it has a value
                                            if (formState.movement !== null && formState.movement !== undefined) {
                                                conditions.push(`movement = ${formState.movement}`)
                                            }

                                            // Add eye beacon conditions if they have values
                                            eyeBeaconFields.forEach(field => {
                                                if (field.value !== null && field.value !== undefined && field.value !== '') {
                                                    conditions.push(`${field.fieldName} = ${field.value}`)
                                                }
                                            })

                                            if (conditions.length === 0) {
                                                return <span style={{ color: '#656d76', fontStyle: 'italic' }}>No conditions set</span>
                                            }

                                            return conditions.join(' AND ')
                                        })()}
                                    </div>
                                </div>
                            </Card>
                        </Form.Item>

                        {/* Submit Button */}
                        <Form.Item style={{ marginTop: '24px' }}>
                            <Button type={data ? 'danger' : 'primary'} htmlType="submit" size="large" style={{ width: '100%' }}>
                                {data ? 'Update' : 'Save'} Device Idle Setting
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form>

        </div>
    )
}

// Export with Form.create() HOC for Ant Design v3
export default Form.create()(IotSetting)